import { fromJS, List, Map } from 'immutable';
import md5 from 'js-md5';
import { Big } from 'big.js';
import {
  concat,
  find,
  flow,
  get,
  head,
  isEmpty,
  isNull,
  map,
  orderBy,
  reduce,
  remove,
  set,
  sortBy,
  sum,
  sumBy,
  values,
} from 'lodash';
import { change, formValueSelector, getFormValues } from 'redux-form/immutable';
import { push, unblock } from 'redux-little-router';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import uniqid from 'uniqid';
import { getReceiptsFileLink } from 'services/API';
import { startRequest } from 'modules/Authorization/redux/logout/actions';
import { requestFailure, requestSuccess } from 'modules/Contacts/redux/contacts/actions';
import * as contactsSelectors from '../../../Contacts/selectors';
import getContacts, {
  getTaxAuthoritiesSuccessors,
} from '../../../Contacts/services/API/getContacts';
import { fileKindMap } from '../../components/NDFL3/constants/FilesKindMap';
import { DEFAULT_REPORT_YEAR, NDFL_FORM_ID } from '../../components/NDFL3/constants/forms';
import { getStepsMapByYear } from '../../components/NDFL3/constants/Ndfl3StepsMapping';
import {
  create3ndflXmlDraftError,
  create3ndflXmlDraftSuccess,
  getCountriesSuccess,
  getDocumentTypesSuccess,
  getIncomeSourceBy2ndflSuccess,
  getTaxAuthoritiesContactsSuccess,
  init3NDFLDraftSuccess,
} from '../../components/NDFL3/NDFL3Actions';
import { getNextOrBackStep } from '../../components/NDFL3/NDFL3ActionsService';
import { sheetFieldsMapping } from '../../components/NDFL3/services/CommonIncomesService';
import { incomeLogicForYear } from '../../components/NDFL3/services/NDFL3YearsLogic/NDFL3YearsLogic';
import {
  getCountriesList,
  getDocumentTypes,
  getMonthIncomes as getMonthIncomesRequest,
  getSocDeductionsBy2NDFL as getSocDeductionsBy2NDFLRequest,
  getStdDeductionsBy2NDFL as getStdDeductionsBy2NDFLRequest,
  new3NDFLDraft,
  save3NDFLAsDraft,
} from '../../services/API';
import getCurrencyRates from '../../services/API/currencyRates';
import get2ndflList from '../../services/API/get2ndflList';
import getIncomeSourcesBy2Ndfl from '../../services/API/getIncomeSourcesBy2Ndfl';
import {
  calculateDeduction as calculateDeductionAction,
  loadingFinish,
  loadingFromNDFL2Finish,
  loadingFromNDFL2Start,
  loadingStart,
  save3NDFLAttachesError,
  save3NDFLAttachesSuccess,
  saveAndGoNextStepAction,
  updateAllDependentFields as updateAllDependentFieldsAction,
} from './NDFL3SagaActions';
import { update3NDFLFormDraft } from './NDFL3SagaEffects';
import { calculateSourceTaxes, setIdsForArray } from './Ndfl3SagaService';
import moment from '../../../../utils/moment';
import subCategoriesMap from '../../constants/subcategoriesMap';
import { create3NDFLXmlDraft } from 'modules/Taps/services/API';
import { setDeductionsFrom2NDFL } from 'modules/Taps/redux/ndfl3Saga/NDFL3SagaEffects';
import { SAGA_ACTIONS } from 'modules/Taps/redux/ndfl3Saga/NDFL3SagaActionTypes';

const selector = formValueSelector(NDFL_FORM_ID);

export function* loadInitialDictionaries() {
  const countriesListResp = yield call(getCountriesList);
  yield put(getCountriesSuccess(countriesListResp.data));

  const documentTypesResp = yield call(getDocumentTypes);
  yield put(getDocumentTypesSuccess(documentTypesResp.data));

  const [contactsResponse, successorsResponse] = yield all([
    call(getContacts),
    call(getTaxAuthoritiesSuccessors),
  ]);

  if (contactsResponse.isOk && successorsResponse.isOk) {
    yield put(requestSuccess(contactsResponse.data, successorsResponse.data));
  } else {
    yield put(requestFailure(contactsResponse.error));
  }

  const activeTaxAuthorities = yield select(contactsSelectors.getIsActiveTaxAuthorities);
  const inspection = yield select(contactsSelectors.getTaxAuthorityByRegistration);
  yield put(getTaxAuthoritiesContactsSuccess({ activeTaxAuthorities, inspection }));
  if (inspection) {
    yield put(change(NDFL_FORM_ID, `Ndfl3Package.taxInspectionCode`, inspection.code));
  } else {
    const activeTaxAuthorities = yield select(contactsSelectors.getIsActiveTaxAuthorities);
    const defaultTaxCode = get(activeTaxAuthorities, '[0].code');
    const currentState = yield select((state) => state);
    if (!formValueSelector(NDFL_FORM_ID)(currentState, 'Ndfl3Package.taxInspectionCode')) {
      yield put(change(NDFL_FORM_ID, `Ndfl3Package.taxInspectionCode`, defaultTaxCode));
    }
  }
}

export function* updateIncomeRates() {
  const sagaFunction = function* (action) {
    const { index } = action.payload;
    const response = yield call(getCurrencyRates, action.payload);
    if (response.isOk && response.data.rate) {
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].incomeCurrencyUnits`,
          response.data.count,
        ),
      );
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].incomeCurrencyUnitsRub`,
          Number(response.data.rate.toFixed(4)),
        ),
      );
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].incomeCurrencyRate`,
          response.data.rate,
        ),
      );
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].incomeCurrencyRateError`,
          null,
        ),
      );
    } else {
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].incomeCurrencyRateError`,
          'Значение отсутствует в справочнике. Введите значение вручную',
        ),
      );
    }
  };
  yield takeLatest(SAGA_ACTIONS.UPDATE_INCOME_RATES, sagaFunction);
}

export function* updateTaxPaymentRates() {
  const sagaFunction = function* (action) {
    const { index } = action.payload;
    const response = yield call(getCurrencyRates, action.payload);
    if (response.isOk && response.data.rate) {
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].paymentCurrencyUnits`,
          response.data.count,
        ),
      );
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].paymentCurrencyUnitsRub`,
          Number(response.data.rate.toFixed(4)),
        ),
      );
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].paymentCurrencyRate`,
          response.data.rate,
        ),
      );
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].paymentCurrencyRateError`,
          null,
        ),
      );
    } else {
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetB.sources[${index}].paymentCurrencyRateError`,
          'Значение отсутствует в справочнике. Введите значение вручную',
        ),
      );
    }
  };
  yield takeLatest(SAGA_ACTIONS.UPDATE_TAX_PAYMENT_RATES, sagaFunction);
}

/**
 * Sign attachments in GP3 and save declaration draft
 * @param ndfl3Package
 * @returns {IterableIterator<IterableIterator<*>|*>}
 */
export function* save3NDFLAttachesSaga({ payload: ndfl3Package }) {
  const reportYear = get(ndfl3Package, 'payload.initialData.reportYear');
  const response = yield submit3NDFLXmlDraft({ ndfl3Package, reportYear });

  if (response.isOk) {
    yield put(save3NDFLAttachesSuccess());
  } else {
    yield put(save3NDFLAttachesError());
  }
}

export function* save3NDFLDraft() {
  yield takeLatest(SAGA_ACTIONS.SAVE_NDFL3_DRAFT_ATTACHES, save3NDFLAttachesSaga);
}

// setting form id for backend
export function setCurrentFormId(currentState) {
  const ndfl3Package = selector(currentState, `Ndfl3Package`);
  const reportYear = ndfl3Package.getIn(
    ['payload', 'initialData', 'reportYear'],
    DEFAULT_REPORT_YEAR,
  );
  const stepsMap = getStepsMapByYear(reportYear);
  const stepIndex = getNextOrBackStep(currentState, true);
  const currentFormId = stepsMap[stepIndex].name;
  return ndfl3Package.set('currentFormId', currentFormId);
}

export function* updateReceiptsList() {
  const sagaFunction = function* () {
    let currentState = yield select((state) => state);
    const ndfl3Package = selector(currentState, `Ndfl3Package`);
    const previousAttaches = List.isList(ndfl3Package.getIn(['payload', 'attaches']))
      ? ndfl3Package.getIn(['payload', 'attaches']).toJS()
      : ndfl3Package.getIn(['payload', 'attaches']);
    const attachedReceipts = List.isList(ndfl3Package.getIn(['payload', 'checks']))
      ? ndfl3Package.getIn(['payload', 'checks']).toJS()
      : ndfl3Package.getIn(['payload', 'checks']);
    if (isEmpty(attachedReceipts)) {
      yield put(change(NDFL_FORM_ID, 'Ndfl3Package.payload.digestChecks', ''));
      remove(
        previousAttaches,
        (attach) => get(attach, 'additionalInfo.kind') === fileKindMap.checks,
      );
      yield put(change(NDFL_FORM_ID, 'Ndfl3Package.payload.attaches', [...previousAttaches]));
      yield put(saveAndGoNextStepAction());
    } else {
      yield put(loadingStart());
      const receiptArrayHash = ndfl3Package.getIn(['payload', 'digestChecks']);
      const formReceiptArrayHash = md5(attachedReceipts);
      if (!receiptArrayHash) {
        yield put(change(NDFL_FORM_ID, 'Ndfl3Package.payload.digestChecks', formReceiptArrayHash));
      }
      if (receiptArrayHash !== formReceiptArrayHash) {
        yield put(change(NDFL_FORM_ID, 'Ndfl3Package.payload.digestChecks', formReceiptArrayHash));
        const response = yield call(
          getReceiptsFileLink,
          map(
            orderBy(
              map(attachedReceipts, (attaches) => ({
                ...attaches,
                originDate: moment(attaches.originDate).startOf('day'),
                categoryId: subCategoriesMap[attaches.subCategory],
              })),
              ['categoryId', 'originDate'],
              ['asc', 'asc'],
            ),
            (check) => check.receiptId,
          ),
        );
        if (response.isOk) {
          remove(
            previousAttaches,
            (attach) => get(attach, 'additionalInfo.kind') === fileKindMap.checks,
          );
          yield put(
            change(NDFL_FORM_ID, 'Ndfl3Package.payload.attaches', [
              {
                fileLink: response.data,
                fileName: `Чеки для подтверждения вычетов`,
                additionalInfo: { kind: fileKindMap.checks, description: 'Приложенные чеки' },
              },
              ...previousAttaches,
            ]),
          );
        }
      }
      yield put(loadingFinish());
      yield put(saveAndGoNextStepAction());
    }
  };
  yield takeLatest(SAGA_ACTIONS.UPDATE_RECEIPTS_LIST, sagaFunction);
}

/**
 * Add income sources by specified 2NDFL
 * @param id
 * @param name
 * @returns {IterableIterator<{type: *}|AxiosPromise|Object|*>}
 */
export function* addIncomeSourcesBy2NDFLId({ payload: { id, name } }) {
  yield put(loadingFromNDFL2Start());

  const currentState = yield select((state) => state);
  const reportYear = selector(currentState, 'Ndfl3Package.payload.initialData.reportYear');
  const ndfl3 = getFormValues(NDFL_FORM_ID)(currentState);
  let sheetA = ndfl3.getIn(['Ndfl3Package', 'payload', 'sheetA']);
  let formSources = ndfl3.getIn(['Ndfl3Package', 'payload', 'sheetA', 'sources'], List());
  const { data: incomeSource } = yield get2ndflList(reportYear, id, name);

  if (!incomeSource) {
    yield loadingFromNDFL2Finish();
    return;
  }

  let avansPlatFix = get(incomeSource, 'advancePayments', 0);

  set(incomeSource, 'id', uniqid('incomeSource'));
  get(incomeSource, 'incomes', []).forEach((item) => {
    set(item, 'id', uniqid('income'));
    setIdsForArray(item.agentDeductions, 'agentDeductions');
    if (!get(item, 'soldProperty')) {
      set(item, 'soldProperty', incomeLogicForYear.initialIncomeFieldsClear);
    }
  });

  delete incomeSource.taxAmountCalculated;

  formSources = formSources.concat(fromJS(incomeSource));

  yield put(getIncomeSourceBy2ndflSuccess(incomeSource));

  if (avansPlatFix > 0) {
    sheetA = sheetA.set('migrantWorker', true);
    sheetA = sheetA.set('totalAdvancePayments', avansPlatFix);
  }

  sheetA = sheetA.set('sources', calculateSourceTaxes(formSources, null, true));
  yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetA`, sheetA));
  yield put(loadingFromNDFL2Finish());
}

export const getIncomeSourceBy2ndflId = function* () {
  yield takeLatest(SAGA_ACTIONS.GET_INCOME_BY_NDFL2_ID, addIncomeSourcesBy2NDFLId);
};

/**
 * Add income sources by all available 2NDFL
 * @returns {IterableIterator<AxiosPromise|Object|*>}
 */
export function* addIncomeSourcesBy2NDFL() {
  yield put(loadingFromNDFL2Start());
  const currentState = yield select((state) => state);
  const { ndfl2 } = currentState.taps.ndfl3;
  const ndfl3 = getFormValues(NDFL_FORM_ID)(currentState);
  let sheetA = ndfl3.getIn(['Ndfl3Package', 'payload', 'sheetA']);
  let formSources = ndfl3.getIn(['Ndfl3Package', 'payload', 'sheetA', 'sources'], List());
  let avansPlatFix = 0;

  if (ndfl2.length) {
    // По справкам 2НДФЛ создаем источники дохода
    const list = ndfl2.map(({ ndfl2Id, companyName }) => ({ id: ndfl2Id, agent: companyName }));
    const { data: incomeSources, isOk } = yield getIncomeSourcesBy2Ndfl(list);
    // set deductionSelector from available 2NDFL list
    yield setDeductionsFrom2NDFL(ndfl2);

    const newSources = flow(
      (incomeSources) =>
        map(incomeSources, (source) => {
          const advancePayments = get(source, 'advancePayments', 0);

          set(source, 'id', uniqid('incomeSource'));
          set(source, 'sumOfFixedAdvancePayments', advancePayments);
          avansPlatFix += advancePayments;

          get(source, 'incomes', []).forEach((item) => {
            set(item, 'id', uniqid('income'));
            setIdsForArray(item.agentDeductions, 'agentDeductions');
            if (!get(item, 'soldProperty')) {
              set(item, 'soldProperty', incomeLogicForYear.initialIncomeFieldsClear);
            }
            if (!get(item, 'taxDeductionCode')) {
              set(item, 'taxDeductionCode', 0);
            }
          });

          delete source.taxAmountCalculated;
          return fromJS(source);
        }),
      (sources) => reduce(sources, (res, item) => res.push(item), List()),
    )(incomeSources);

    formSources = formSources.concat(newSources);

    yield put(getIncomeSourceBy2ndflSuccess(incomeSources));

    if (!isOk || !incomeSources) {
      yield put(loadingFromNDFL2Finish());
      return;
    }
  }

  if (avansPlatFix > 0) {
    sheetA = sheetA.set('migrantWorker', true);
    sheetA = sheetA.set('totalAdvancePayments', avansPlatFix);
  }

  yield put(
    change(
      NDFL_FORM_ID,
      `Ndfl3Package.payload.sheetA`,
      sheetA.set('sources', calculateSourceTaxes(formSources, null, true)),
    ),
  );
  yield put(loadingFromNDFL2Finish());
}

export function* save3NDFLasDraftAndExit() {
  function* sagaFunction(action) {
    let currentState = yield select((state) => state);
    let ndfl3Package = selector(currentState, `Ndfl3Package`).set('currentFormId', '');
    const { logout } = currentState.taps.ndfl3;
    const year = ndfl3Package.getIn(['payload', 'initialData', 'reportYear']);
    yield call(save3NDFLAsDraft, ndfl3Package, year);
    yield all([
      put(unblock()),
      logout ? put(startRequest()) : put(push(action.payload.blockLocation)),
    ]);
  }

  yield takeLatest(SAGA_ACTIONS.SAVE_AND_EXIT, sagaFunction);
}

export const getAgents = (currentState) => {
  const data = {};
  const ndfl3 = getFormValues(NDFL_FORM_ID)(currentState);
  const sources = ndfl3.getIn(['Ndfl3Package', 'payload', 'sheetA', 'sources'], List());

  // Делаем выборку доходов где есть origin
  sources.forEach((source) => {
    const incomes = source.get('incomes');
    incomes.forEach((income) => {
      const origin = income.get('origin');
      if (origin) {
        const id = origin.split(':')[0];
        const agent = origin.split(':')[1];
        data[agent] = { id, agent };
      }
    });
  });

  return values(data);
};

/**
 * Submit 3NDFL to 3GP container and sign form attaches
 * inventoryXml should be returned in the server response if attaches are not empty
 * @param {Object} ndfl3Package
 * @param {Number} reportYear
 * @returns {IterableIterator<*>}
 */
export function* submit3NDFLXmlDraft({ ndfl3Package, reportYear }) {
  const response = yield call(create3NDFLXmlDraft, ndfl3Package, reportYear);
  if (!response.isOk) {
    yield put(create3ndflXmlDraftError(response));
  } else {
    yield put(create3ndflXmlDraftSuccess(response.data));
  }
  return response;
}

export function* getSocDeductionsBy2NDFL() {
  const currentState = yield select((state) => state);
  const reportYear = get(currentState.taps.ndfl3, 'ndfl3Package.payload.initialData.reportYear');
  const sheetE2 = (selector(currentState, `Ndfl3Package.payload.sheetE2`) || new Map()).toJS();

  const agents = getAgents(currentState);
  if (!isEmpty(agents)) {
    // По справкам 2НДФЛ запрашиagents социальные налоговые вычеты
    const response = yield call(getSocDeductionsBy2NDFLRequest, values(agents), reportYear);

    if (response.isOk) {
      const { data } = response;

      if (!isEmpty(data)) {
        if (!get(sheetE2, 'charityLiveDeduction.deductionAmount') && data.cttDeduction) {
          yield put(
            change(
              NDFL_FORM_ID,
              `Ndfl3Package.payload.sheetE2.charityLiveDeduction.deductionAmount`,
              data.cttDeduction,
            ),
          );
          yield put(
            change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE2.charityLiveDeductionSelected`, true),
          );
        }

        if (!get(sheetE2, 'pensContribDeduction.deductionAmount') && data.pensContribDeductionSum) {
          yield put(
            change(
              NDFL_FORM_ID,
              `Ndfl3Package.payload.sheetE2.pensContribDeduction.deductionAmount`,
              data.pensContribDeductionSum,
            ),
          );
          yield put(
            change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE2.pensContribDeductionSelected`, true),
          );
        }

        if (
          !get(sheetE2, 'pensInsDeduction.contracts[0].organizationInfo.deductionAmount') &&
          data.pensInsDeductionSum
        ) {
          yield put(
            change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE2.pensInsDeductionsSelected`, true),
          );
        }

        yield put({
          type: SAGA_ACTIONS.GET_SOC_DEDUCTIONS_SUCCESS,
          payload: {
            ndfl2Entries: data.ndfl2Entries,
            pensInsDeductionSum: data.pensInsDeductionSum,
          },
        });
      }
    }
  }
}

export function* getStdDeductionsBy2NDFL() {
  const currentState = yield select((state) => state);
  const reportYear = get(currentState.taps.ndfl3, 'ndfl3Package.payload.initialData.reportYear');
  const agentDeductionSelected = selector(
    currentState,
    'Ndfl3Package.payload.sheetE1.agentDeductionSelected',
  );
  const userDeductionSelected = selector(
    currentState,
    'Ndfl3Package.payload.sheetE1.userDeductionSelected',
  );
  const agentDeductionManual = selector(
    currentState,
    'Ndfl3Package.payload.sheetE1.agentDeductionManual',
  );
  const data = getAgents(currentState);

  if (!isEmpty(data)) {
    // По справкам 2НДФЛ запрашиваем стандартные налоговые вычеты
    const response = yield call(getStdDeductionsBy2NDFLRequest, data, reportYear);
    const deductions = get(response, 'data.deductions', []);
    yield put({
      type: SAGA_ACTIONS.GET_STD_DEDUCTIONS_SUCCESS,
      payload: {
        stdDeductionsBy2NDFL: deductions,
      },
    });

    // Сведения о стандартных вычетах по данным налогового агента
    if (!isEmpty(deductions) && !isEmpty(head(deductions).deductions)) {
      if (isNull(agentDeductionManual)) {
        yield put(
          change(
            NDFL_FORM_ID,
            `Ndfl3Package.payload.sheetE1.agentDeductionManual`,
            sum(deductions.map(({ deductions }) => sumBy(deductions, ({ sum }) => sum))),
          ),
        );
      }
      yield put(
        change(
          NDFL_FORM_ID,
          `Ndfl3Package.payload.sheetE1.agentDeduction.stdDeductions`,
          fromJS(deductions),
        ),
      );

      // Устанавливает по умолчанию значение - "По данным налогового агента"
      if (!agentDeductionSelected && !userDeductionSelected) {
        yield put(
          change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.agentDeductionSelected`, true),
        );
      }
    } else {
      if (isNull(agentDeductionManual)) {
        yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.agentDeductionManual`, ''));
      }
      yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.userDeductionSelected`, true));
      yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.agentDeductionSelected`, false));
    }
  } else {
    if (isNull(agentDeductionManual)) {
      yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.agentDeductionManual`, ''));
    }
    yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.userDeductionSelected`, true));
    yield put(change(NDFL_FORM_ID, `Ndfl3Package.payload.sheetE1.agentDeductionSelected`, false));
  }
}

export function* getMonthIncomes() {
  const sagaFunction = function* (action) {
    const { selected = {} } = action.payload;
    let currentState = yield select((state) => state);
    const { ndfl2 } = currentState.taps.ndfl3;
    const stdDeductionChildren = (
      selector(currentState, 'Ndfl3Package.payload.sheetE1.userDeduction.stdDeductionChildren') ||
      Map()
    ).toJS();
    const monthIncomes = (
      selector(
        currentState,
        'Ndfl3Package.payload.sheetE1.userDeduction.stdDeductionChildren.monthIncomes',
      ) || List()
    ).toJS();
    const data = {};
    const reportYear = get(currentState.taps.ndfl3, 'ndfl3Package.payload.initialData.reportYear');

    // По справкам 2НДФЛ создаем источники дохода
    ndfl2.forEach((item, index) => {
      // Если отмечена справка 2НДФЛ

      if (selected[index] === true) {
        data[item.ndfl2Id] = { id: item.ndfl2Id, agent: item.companyName };
      }
    });

    if (!isEmpty(data)) {
      // Вычисляет доходы и вычеты по месяцам на основе переданных данных справок 2-ндфл
      const response = yield call(getMonthIncomesRequest, values(data), reportYear);

      const { data: responseData, error } = response;

      // если запросили данные из нескольких справок и будет пересечение по месяцам то бэкенд вернет ошибку
      if (error) {
        yield put({
          type: SAGA_ACTIONS.GET_MONTH_INCOMES_RESULT,
          payload: {
            error: get(error, 'response.data.reason.description'),
          },
        });
        return;
      }

      // проверка на отсутствие пересечения по месяцам
      if (!isEmpty(responseData) && !error) {
        const isDuplicationOfMonths = responseData.some((item) => {
          return find(monthIncomes, { month: item.month });
        });

        if (isDuplicationOfMonths) {
          yield put({
            type: SAGA_ACTIONS.GET_MONTH_INCOMES_RESULT,
            payload: {
              error:
                'Выбранные справки содержат сведения о доходах за один и тот же месяц.' +
                ' Необходимо выбрать доходы от одного работодателя',
            },
          });
          return;
        }
      }

      if (!isEmpty(responseData)) {
        const ndfl2Ids = [];
        // сохраняем загруженные ID справок
        ndfl2.forEach((item, index) => {
          // Если отмечена справка 2НДФЛ
          if (selected[index]) {
            ndfl2Ids.push(`${item.ndfl2Id}:${item.companyName}`);
          }
        });

        stdDeductionChildren.monthIncomes = [
          ...monthIncomes,
          ...responseData.map((income) => {
            if (!income.deductions.length) {
              income.deductions = [{ deductionSum: 0, deductionTypeCode: 0 }];
            }
            return income;
          }),
        ];
        stdDeductionChildren.ndfl2Ids = ndfl2Ids;

        yield put(
          change(
            NDFL_FORM_ID,
            `Ndfl3Package.payload.sheetE1.userDeduction.stdDeductionChildren`,
            fromJS(stdDeductionChildren),
          ),
        );

        yield put({
          type: SAGA_ACTIONS.GET_MONTH_INCOMES_RESULT,
          payload: {
            error: '',
          },
        });
      } else {
        yield put({
          type: SAGA_ACTIONS.GET_MONTH_INCOMES_RESULT,
          payload: {
            error: '',
          },
        });
      }
    }
  };

  yield takeLatest(SAGA_ACTIONS.GET_MONTH_INCOMES, sagaFunction);
}

export const init3NDFLDraft = function* () {
  const response = yield call(new3NDFLDraft);
  if (response.isOk) {
    set(response.data, 'payload.incomeSelector.inRF', true);
    set(response.data, 'payload.incomeSelector.outRF', true);
    yield update3NDFLFormDraft(response.data);
    yield put(init3NDFLDraftSuccess(response.data));
  }
};

export function* calculateTotalIncomeSums() {
  function* sagaFunction(action) {
    const currentState = yield select((state) => state);
    const { incomeSourceIndex } = action.payload;
    const ndfl3 = getFormValues(NDFL_FORM_ID)(currentState);
    const source = ndfl3.getIn(
      ['Ndfl3Package', 'payload', 'sheetA', 'sources', incomeSourceIndex],
      Map(),
    );
    const taxRate = source.get('taxRate');
    const [calculatedSource] = calculateSourceTaxes([source], taxRate);

    yield put(
      change(
        NDFL_FORM_ID,
        `Ndfl3Package.payload.sheetA.sources[${incomeSourceIndex}]`,
        calculatedSource,
      ),
    );
  }

  yield takeLatest(SAGA_ACTIONS.CALCULATE_SUMS_SECTION, sagaFunction);
}

export function* updateGameDeduction(incomeSourceIndex, incomeIndex) {
  const isSheetA = incomeIndex || incomeIndex === 0;
  const formFields = isSheetA
    ? sheetFieldsMapping('sheetA', incomeSourceIndex, incomeIndex)
    : sheetFieldsMapping('sheetB', incomeSourceIndex);
  const currentState = yield select((state) => state);
  const taxDeductionCodeValue = selector(currentState, formFields.taxDeductionCode);
  const incomeSum = +selector(currentState, formFields.incomeAmountRub) || 0;
  const incomeSumNum = parseFloat(incomeSum);

  const { totalDeductionSum } = getAllIncomes(
    currentState,
    [3010, 3011],
    taxDeductionCodeValue,
    0,
    incomeSourceIndex,
    incomeIndex,
  );
  const limitBY = 4000;

  let resultDeductionSum = 0;

  if (totalDeductionSum > limitBY) {
    resultDeductionSum = 0;
  } else {
    const deductionLeft = limitBY - totalDeductionSum > 0 ? limitBY - totalDeductionSum : 0;
    resultDeductionSum = deductionLeft > incomeSumNum ? incomeSumNum : deductionLeft;
  }
  return yield put(change(NDFL_FORM_ID, formFields.deductionSum, resultDeductionSum.toFixed(2)));
}

export function getAllFieldByIncomeTypes(taxCodeArray = [], currentState) {
  const sheetAsources = selector(currentState, `Ndfl3Package.payload.sheetA.sources`);
  const sheetBsources = selector(currentState, `Ndfl3Package.payload.sheetB.sources`);
  const result = [];
  sheetAsources.forEach((source, incomeSourceIndex) => {
    return source.get('incomes', List()).forEach((income, incomeIndex) => {
      const formFields = sheetFieldsMapping('sheetA', incomeSourceIndex, incomeIndex);
      const incomeCode = selector(currentState, formFields.incomeTypeCode);
      if (taxCodeArray.includes(incomeCode)) {
        const formFields = sheetFieldsMapping(
          'sheetA',
          incomeSourceIndex,
          incomeIndex,
        ).deductionSum;
        result.push({ incomeSourceIndex, formFields, incomeIndex, sheet: 'A' });
      }
    });
  });
  sheetBsources.forEach((source, incomeSourceIndex) => {
    const formFields = sheetFieldsMapping('sheetB', incomeSourceIndex);
    const incomeCode = selector(currentState, formFields.incomeTypeCode);
    if (taxCodeArray.includes(incomeCode)) {
      const formFields = sheetFieldsMapping('sheetB', incomeSourceIndex).deductionSum;
      result.push({ incomeSourceIndex, formFields, incomeIndex: undefined, sheet: 'B' });
    }
  });
  return result;
}

export function getAllIncomes(
  currentState,
  incomeType,
  deductionType,
  limit,
  curIncomeSourceIndex,
  curIncomeIndex,
) {
  const sheetAsources = selector(currentState, `Ndfl3Package.payload.sheetA.sources`);
  const sheetBsources = selector(currentState, `Ndfl3Package.payload.sheetB.sources`);

  let totalIncomeSum = 0;
  let totalDeductionSum = 0;

  sheetAsources.map((source, incomeSourceIndex) => {
    source.get('incomes', List()).forEach((income, incomeIndex) => {
      const formFields = sheetFieldsMapping('sheetA', incomeSourceIndex, incomeIndex);
      const taxDeductionCodeValue = selector(currentState, formFields.taxDeductionCode);
      const incomeCode = selector(currentState, formFields.incomeTypeCode);
      const incomeSum = +selector(currentState, formFields.incomeAmountRub) || 0;
      const deductionSum = +selector(currentState, formFields.deductionSum) || 0;

      if (incomeType.includes(incomeCode) && deductionType === taxDeductionCodeValue) {
        totalIncomeSum += incomeSum;
        if (incomeSourceIndex === curIncomeSourceIndex && incomeIndex === curIncomeIndex) return;
        totalDeductionSum += deductionSum;
      }
    });
  });

  sheetBsources.map((source, incomeSourceIndex) => {
    const formFields = sheetFieldsMapping('sheetB', incomeSourceIndex);
    const taxDeductionCodeValue = selector(currentState, formFields.taxDeductionCode);
    const incomeCode = selector(currentState, formFields.incomeTypeCode);
    const incomeSum = +selector(currentState, formFields.incomeAmountRub) || 0;
    const deductionSum = +selector(currentState, formFields.deductionSum) || 0;

    if (incomeType.includes(incomeCode) && deductionType === taxDeductionCodeValue) {
      totalIncomeSum += incomeSum;
      if (incomeSourceIndex === curIncomeSourceIndex && typeof curIncomeIndex === 'undefined')
        return;
      totalDeductionSum += deductionSum;
    }
  });

  return { totalIncomeSum, totalDeductionSum };
}

export function* calculateDeduction() {
  function* sagaFunction(action) {
    const { incomeSourceIndex, incomeIndex } = action.payload;
    const isSheetA = incomeIndex || incomeIndex === 0;
    const formFields = isSheetA
      ? sheetFieldsMapping('sheetA', incomeSourceIndex, incomeIndex)
      : sheetFieldsMapping('sheetB', incomeSourceIndex);
    const currentState = yield select((state) => state);

    const taxDeductionCodeValue = selector(currentState, formFields.taxDeductionCode);
    const incomeSum = +selector(currentState, formFields.incomeAmountRub) || 0;
    let resultDeductionSum = 0;
    const incomeCode = selector(currentState, formFields.incomeTypeCode);

    if (incomeCode === 3010 || incomeCode === 3011) {
      const fields = sortBy(getAllFieldByIncomeTypes([3010, 3011], currentState), (field) => {
        return field.incomeSourceIndex === incomeSourceIndex && field.incomeIndex === incomeIndex
          ? -1
          : 0;
      });
      return yield put(updateAllDependentFieldsAction(fields));
    }

    if (taxDeductionCodeValue && incomeSum) {
      const numChildren = +selector(currentState, formFields.numChildren) || 0;
      const shareNumerator = +selector(currentState, formFields.shareNumerator) || 0;
      const shareDenominator = +selector(currentState, formFields.shareDenominator) || 0;
      const deductionTypes = get(currentState, 'taps.ndfl3.dictionaries.deductionTypes.data');

      const deductionType = find(
        deductionTypes,
        (item) => item.deductionCode === taxDeductionCodeValue && item.incomeCode === incomeCode,
      );
      const limit = get(deductionType, 'limit');
      const maxPercent = get(deductionType, 'maxPercent');
      const incomeSumNum = parseFloat(incomeSum);

      let shouldUpdate = true;
      if (incomeCode === 2799 && taxDeductionCodeValue === 599) {
        resultDeductionSum = incomeSumNum;
      }
      // Если в поле «Вид дохода» выбран доход одного из видов 2201-2209 и в поле
      // «Предоставить налоговый вычет» выбран вычет вида «405 – в пределах норматива расходов».
      // Заполняется значением, рассчитанным по формуле:
      // <значение поля «Сумма дохода»> * <значение атрибута «Размер вычета по нормативу»> / 100
      else if (
        taxDeductionCodeValue === 405 &&
        [2201, 2202, 2203, 2204, 2205, 2206, 2207, 2208, 2209].includes(incomeCode)
      ) {
        if (maxPercent && incomeSumNum) {
          resultDeductionSum = Number((incomeSumNum * maxPercent) / 100);
        }
      }
      // 1.
      // Для вычета вида «508-Вычет из суммы единоврем. мат. помощи при рождении (усынов.) ребенка»:
      //  Если поле «Количество детей» заполнено, то рассчитывается по формуле
      //  <значение атрибута «максимальный размер вычета»>*<значение поля «Количество детей»>
      //  Если поле «Количество детей» не заполнено, значение рассчитывается аналогично другим видам вычетов.
      else if (taxDeductionCodeValue === 508 && parseInt(numChildren) > 0) {
        resultDeductionSum = limit * parseInt(numChildren);
      } else if (limit) {
        const { totalDeductionSum: totalDeductionSumWithoutCur, totalIncomeSum } = getAllIncomes(
          currentState,
          [incomeCode],
          taxDeductionCodeValue,
          limit,
          incomeSourceIndex,
          incomeIndex,
        );

        if (
          taxDeductionCodeValue === 904 &&
          [1511].includes(incomeCode) &&
          shareNumerator &&
          shareDenominator
        ) {
          // Если поле «Размер доли в праве» или «Сумма дохода» не заполнено, значение по умолчанию не заполняется
          resultDeductionSum = Number(limit * (shareNumerator / shareDenominator));
        }
        // 2.
        // Для других видов вычетов:
        //  значение атрибута «Максимальный размер вычета» из справочника «Налоговые вычеты и суммы,
        //  уменьшающие налоговую базу» для вычета, выбранного в поле «Предоставить налоговый вычет»,
        //  если указанное значение непустое и не превышает сумму, указанную пользователем в поле «Сумма дохода».
        else if (limit <= totalIncomeSum) {
          resultDeductionSum = limit - totalDeductionSumWithoutCur;
        }
        // 3.
        // Если атрибут «Максимальный размер вычета» пуст или не введена сумма дохода – пусто.
        else if (!limit || !totalIncomeSum) {
          resultDeductionSum = 0;
        }
        // 4.
        // Если значение атрибута «Максимальная сумма вычета» превышает значение поля «Сумма дохода»,
        // то поле заполняется значением поля «Сумма дохода» соответствующего дохода.
        else {
          resultDeductionSum = incomeSumNum || null;
        }

        if (resultDeductionSum > limit - totalDeductionSumWithoutCur) {
          resultDeductionSum = limit - totalDeductionSumWithoutCur;
        }
      } else if (!limit) {
        shouldUpdate = false;
      }

      if (resultDeductionSum && incomeSum) {
        // prettier-ignore
        const incomeCodesCanBeMoreThanIncomeSum = [1544, 1545, 1546, 1547, 1548, 1549, 1551, 9551, 1552, 9552,
          1530, 1536, 1531, 1532, 1533, 1535, 1537, 9537, 1538, 9538, 9991, 9981, 9992, 9982, 9993,
          9983, 9994, 9984, 9995, 9985, 9996, 9986, 1541, 1539, 1553, 1554];

        if (
          !incomeCodesCanBeMoreThanIncomeSum.includes(incomeCode) &&
          resultDeductionSum > incomeSum
        ) {
          // Если поле «Размер доли в праве» заполнено и значение атрибута «максимальный размер вычета» для данного вычета,
          // умноженное на значение поля «Размер доли в праве», меньше или равно значению поля «Сумма дохода»,
          // то рассчитывается по формуле <значение атрибута «максимальный размер вычета»>*<значение поля «Размер доли в праве»>
          resultDeductionSum = incomeSum;
        }
      }
      if (shouldUpdate && (resultDeductionSum || resultDeductionSum === 0)) {
        yield put(change(NDFL_FORM_ID, formFields.deductionSum, resultDeductionSum.toFixed(2)));
      }
    }
  }

  yield takeLatest(SAGA_ACTIONS.CALCULATE_DEDUCTION, sagaFunction);
}

export function* updateDependentGameDeductionFields() {
  function* sagaFunction(action) {
    const fieldsArray = concat([], action.payload);

    while (!isEmpty(fieldsArray)) {
      const firstField = fieldsArray.shift();
      yield call(updateGameDeduction, firstField.incomeSourceIndex, firstField.incomeIndex);
    }
  }

  yield takeEvery(SAGA_ACTIONS.UPDATE_ALL_DEPENDENT_GAME_DEDUCTION_FIELDS, sagaFunction);
}

/**
 * Updates incomes sources deductions by income type code
 * based on predefined limits
 * @param incomeTypeCode
 * @returns {Generator<*, void, ?>}
 */
export function* updateDeductionsSaga({ payload: { incomeTypeCode } }) {
  const currentState = yield select((state) => state);
  const sheetASources = selector(currentState, `Ndfl3Package.payload.sheetA.sources`);
  const sheetBSources = selector(currentState, `Ndfl3Package.payload.sheetB.sources`);
  const applicableSheetASources = [];

  sheetASources.forEach((source, incomeSourceIndex) => {
    const incomes = source.get('incomes');

    incomes.forEach((income, incomeIndex) => {
      if (
        (income.get('incomeTypeCode') === incomeTypeCode ||
          income.get('incomeTypeCodeASVK') === incomeTypeCode) &&
        income.get('taxDeductionCode') &&
        Big(income.get('deductionSum')).lt(income.get('incomeSum'))
      ) {
        applicableSheetASources.push({
          incomeSourceIndex,
          incomeIndex,
        });
      }
    });
  });

  const applicableSheetBSources = [];

  sheetBSources.forEach((source, incomeSourceIndex) => {
    if (
      (source.get('incomeTypeCode') === incomeTypeCode ||
        source.get('incomeTypeCodeASVK') === incomeTypeCode) &&
      source.get('taxDeductionCode') &&
      Big(source.get('deductionSum')).lt(source.get('incomeAmountRub'))
    ) {
      applicableSheetBSources.push(incomeSourceIndex);
    }
  });

  yield all(
    applicableSheetASources.map(({ incomeSourceIndex, incomeIndex }) =>
      put(
        calculateDeductionAction({
          incomeSourceIndex,
          incomeIndex: ~incomeIndex ? incomeIndex : undefined,
        }),
      ),
    ),
  );

  yield all(
    applicableSheetBSources.map((incomeSourceIndex) =>
      put(
        calculateDeductionAction({
          incomeSourceIndex,
        }),
      ),
    ),
  );
}

export function* updateDeductionsByIncomeTypeCode() {
  yield takeLatest(
    SAGA_ACTIONS.UPDATE_CALCULATED_DEDUCTIONS_BY_INCOME_TYPE_CODE,
    updateDeductionsSaga,
  );
}

export function* updateInvestmentTaxDeduction(ndfl3Package) {
  const sum = get(ndfl3Package, 'payload.investmentTaxDeduction.sum');
  const sumOfSimplifiedTaxDeduction = get(
    ndfl3Package,
    'payload.investmentTaxDeduction.sumOfSimplifiedTaxDeduction',
  );

  // Блок полей ИИС отображается только если указана 'Сумма вычета' или 'Сумма вычета, предоставленная в упрощенном порядке',
  // иначе очищаем из iis все возможные черновые значения
  if (!sum && !sumOfSimplifiedTaxDeduction) {
    yield put(change(NDFL_FORM_ID, 'Ndfl3Package.payload.investmentTaxDeduction.iis', fromJS([])));
  }
}
